// Observable app store + pure selectors over its data (task filters, stats, progress, levels, routes).
// Everything here is DOM-free so it can be unit-tested with node:test.
import { floorNumber } from './scene/layout.js';

/**
 * A small observable store. `set(patch)` shallow-merges top-level keys and notifies listeners of
 * the keys whose value changed (by identity) — so always replace objects/Maps instead of mutating.
 * @param {object} initial
 * @returns {{get(): object, set(patch: object): string[], update(key: string, fn: Function): void,
 *   on(key: string, fn: (value, state, prev) => void): () => void, batch(fn: Function): void}}
 */
/** True when `next` is strictly older than `prev` (both must carry an updatedAt). */
export function isOlder(next, prev) {
  if (!next || !prev) return false;
  const a = timeOf(next.updatedAt);
  const b = timeOf(prev.updatedAt);
  return a !== null && b !== null && a < b;
}

function timeOf(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v) { const n = Date.parse(v); return Number.isFinite(n) ? n : null; }
  return null;
}

export function createStore(initial = {}) {
  let state = { ...initial };
  const listeners = new Map();
  let batching = 0;
  let pending = null;

  function emit(changed, prev) {
    for (const key of changed) {
      for (const fn of [...(listeners.get(key) || [])]) {
        try { fn(state[key], state, prev); } catch (err) { console.error('[store]', key, err); }
      }
    }
    for (const fn of [...(listeners.get('*') || [])]) {
      try { fn(state, prev, changed); } catch (err) { console.error('[store] *', err); }
    }
  }

  function set(patch) {
    const changed = [];
    const next = { ...state };
    for (const [k, v] of Object.entries(patch || {})) {
      if (!Object.is(state[k], v)) { next[k] = v; changed.push(k); }
    }
    if (!changed.length) return changed;
    const prev = state;
    state = next;
    if (batching) {
      pending ||= { prev, keys: new Set() };
      for (const k of changed) pending.keys.add(k);
    } else {
      emit(changed, prev);
    }
    return changed;
  }

  return {
    get: () => state,
    set,
    update(key, fn) { set({ [key]: fn(state[key]) }); },
    on(key, fn) {
      if (!listeners.has(key)) listeners.set(key, new Set());
      listeners.get(key).add(fn);
      return () => listeners.get(key)?.delete(fn);
    },
    batch(fn) {
      batching += 1;
      try { fn(); } finally {
        batching -= 1;
        if (!batching && pending) {
          const p = pending;
          pending = null;
          emit([...p.keys], p.prev);
        }
      }
    },
  };
}

/** Default `ui` slice. */
export const DEFAULT_UI = Object.freeze({
  floorId: null,          // current floor
  view: 'floor',          // 'floor' | 'building'
  panel: 'tasks',         // mobile sheet tab: 'tasks' | 'waiting'
  theme: 'light',         // resolved theme
  themePref: 'system',    // 'system' | 'light' | 'dark'
  selectedTaskId: null,
  selectedAgentId: null,
  deptFilter: null,       // department id filtering the task list (click on a card)
  taskFilter: 'all',      // 'all' | 'running' | 'waiting' | 'done' | 'scheduled'
  panelCollapsed: false,
  sheetExpanded: false,
  route: null,            // last parsed route
  hoverId: null,          // {kind, id} hovered in the scene or on a card
  reducedMotion: false,
  sceneFallback: false,   // true when the 3D scene gives no anchors (cards laid out in a grid)
});

/**
 * The app store with its initial shape (see docs/UI-CONTRACT.md) and a few domain helpers.
 */
/** The per-session part of the app state (what one signed-in person may see). */
function sessionState() {
  return {
    me: null,
    csrf: '',
    office: { name: 'Maeck Office', version: '', language: 'fr', mock: false, floors: [], departments: [], agents: [] },
    agentStatus: new Map(),   // agentId → {status, taskId, activity, since}
    tasks: new Map(),         // taskId → TaskSummary (+ _at: client receipt time, ms)
    tasksLoaded: false,
    tasksUnavailable: false,
    routines: [],             // every routine this person may see (GET /api/routines + live `routine` events)
    routinesLoaded: false,
    suggestions: [],          // improvement suggestions: all for an admin, one's own otherwise (GET /api/suggestions + live events)
    suggestionsLoaded: false,
    suggestionsUnavailable: false, // GET /api/suggestions answered 404 (server without the module)
    libraryConnectors: [],    // the office's connector library as this person sees it (GET /api/connectors/library + live events), when features.connectorLibrary
    libraryConnectorsLoaded: false,
    connectors: [],
    engines: {},
    counts: { awaitingApproval: 0, running: 0, queued: 0 },
    stats: { departments: {}, floors: {}, source: 'none' },
    // « À régler » (GET /api/notifications, web/ui/notifications.js): items of every floor seen,
    // counts per floor (the bubble, the elevator), and among them the person's finished requests.
    notices: { items: [], counts: {}, total: 0, doneCounts: {}, doneTotal: 0, loaded: false, unavailable: false },
    features: { atelier: false },
    degraded: false,
  };
}

export function createAppStore() {
  const store = createStore({
    ...sessionState(),
    sse: 'idle',
    serverOffset: 0,          // server clock − client clock (ms), from the SSE hello event
    serverBuild: null,        // the web build the server serves (hello, bootstrap, X-Office-Build): web/ui/updatenotice.js
    ui: { ...DEFAULT_UI },
  });

  const helpers = {
    /**
     * Forgets everything the previous session loaded (tasks, office, counts…) so the next person
     * signing in on this page never sees it; only the theme choice is kept.
     */
    resetSession() {
      const { theme, themePref } = store.get().ui;
      store.set({ ...sessionState(), ui: { ...DEFAULT_UI, theme, themePref } });
    },
    /** Merges a patch into `ui` (no-op when nothing changes). */
    setUi(patch) {
      const ui = store.get().ui;
      let changed = false;
      for (const [k, v] of Object.entries(patch)) if (!Object.is(ui[k], v)) { changed = true; break; }
      if (changed) store.set({ ui: { ...ui, ...patch } });
    },
    /** Inserts/replaces a task summary; returns the previous one (or undefined). */
    upsertTask(summary, now = Date.now()) {
      if (!summary || !summary.id) return undefined;
      const tasks = new Map(store.get().tasks);
      const prev = tasks.get(summary.id);
      tasks.set(summary.id, { ...summary, _at: now });
      store.set({ tasks });
      return prev;
    },
    /** Merges a list of summaries (replace=true drops tasks not in the list). */
    setTasks(list, { replace = false, now = Date.now() } = {}) {
      const tasks = replace ? new Map() : new Map(store.get().tasks);
      for (const s of list || []) if (s && s.id) tasks.set(s.id, { ...s, _at: now });
      store.set({ tasks, tasksLoaded: true });
    },
    removeTask(id) {
      const cur = store.get().tasks;
      if (!cur.has(id)) return undefined;
      const tasks = new Map(cur);
      const prev = tasks.get(id);
      tasks.delete(id);
      store.set({ tasks });
      return prev;
    },
    /** Upserts a task summary and updates the top-bar counts accordingly (use for SSE + POST results). */
    applyTask(summary, now = Date.now()) {
      if (!summary || !summary.id) return undefined;
      // A routine pass with nothing to do (SPEC-EQUIPE §3) is kept out of every list.
      if (summary.idle === true) return helpers.dropTask(summary.id);
      // A late POST response must not undo a newer live event (both carry updatedAt).
      const known = store.get().tasks.get(summary.id);
      if (isOlder(summary, known)) return known;
      let prev;
      store.batch(() => {
        prev = helpers.upsertTask(summary, now);
        store.set({ counts: applyCountsDelta(store.get().counts, prev || null, summary) });
      });
      return prev;
    },
    /** Removes a task and updates the counts. */
    dropTask(id) {
      let prev;
      store.batch(() => {
        prev = helpers.removeTask(id);
        if (prev) store.set({ counts: applyCountsDelta(store.get().counts, prev, null) });
      });
      return prev;
    },
    /** Replaces the routines list (GET /api/routines). */
    setRoutines(list) {
      store.set({ routines: (list || []).filter((r) => r && typeof r.id === 'string'), routinesLoaded: true });
    },
    /** Inserts/replaces one routine (live `routine` event, editor save). */
    upsertRoutine(routine) {
      if (!routine || typeof routine.id !== 'string') return;
      const cur = store.get().routines;
      const i = cur.findIndex((r) => r.id === routine.id);
      store.set({ routines: i < 0 ? [...cur, routine] : cur.map((r, j) => (j === i ? { ...r, ...routine } : r)) });
    },
    /** Forgets one routine (live `routine.removed` event). */
    removeRoutine(id) {
      const cur = store.get().routines;
      if (cur.some((r) => r.id === id)) store.set({ routines: cur.filter((r) => r.id !== id) });
    },
    /** Replaces the suggestions list (GET /api/suggestions). */
    setSuggestions(list) {
      store.set({
        suggestions: (list || []).filter((s) => s && typeof s.id === 'string'), suggestionsLoaded: true, suggestionsUnavailable: false,
      });
    },
    /**
     * Inserts/replaces one suggestion (live `suggestion` event, POST/PATCH answers). The server always
     * sends the whole suggestion. An older copy (a late answer) is ignored, and so is the copy already
     * held (same updatedAt and status: the shell and an open panel may both apply one event).
     */
    upsertSuggestion(suggestion) {
      if (!suggestion || typeof suggestion.id !== 'string') return;
      const cur = store.get().suggestions;
      const i = cur.findIndex((s) => s.id === suggestion.id);
      const known = i < 0 ? null : cur[i];
      const same = !!known && known.updatedAt != null && known.updatedAt === suggestion.updatedAt && known.status === suggestion.status;
      if (known && (same || isOlder(suggestion, known))) return;
      store.set({ suggestions: i < 0 ? [suggestion, ...cur] : cur.map((s, j) => (j === i ? suggestion : s)) });
    },
    /** Forgets one suggestion (live `suggestion.removed` event, DELETE). */
    removeSuggestion(id) {
      const cur = store.get().suggestions;
      if (cur.some((s) => s.id === id)) store.set({ suggestions: cur.filter((s) => s.id !== id) });
    },
    setAgentStatus(agentId, status) {
      const map = new Map(store.get().agentStatus);
      map.set(agentId, { ...(map.get(agentId) || {}), ...normalizeStatus(status), since: Date.now() });
      store.set({ agentStatus: map });
    },
  };
  return Object.assign(store, helpers);
}

/** Accepts 'working' or {status, taskId, activity} and returns the object form. */
export function normalizeStatus(s) {
  if (!s) return { status: 'idle', taskId: null, activity: null };
  if (typeof s === 'string') return { status: s, taskId: null, activity: null };
  return { status: s.status || 'idle', taskId: s.taskId ?? null, activity: s.activity ?? null };
}

// ---------------------------------------------------------------------------------------------
// Tasks

export const ACTIVE_STATES = new Set(['running', 'executing']);
export const UPCOMING_STATES = new Set(['queued', 'scheduled']);
export const FINISHED_STATES = new Set(['done', 'failed', 'rejected', 'cancelled']);
export const ALL_STATES = ['scheduled', 'queued', 'running', 'awaiting_approval', 'executing', 'done', 'failed', 'rejected', 'cancelled'];

/** Filter predicates of the task panel chips. */
export const TASK_FILTERS = {
  all: () => true,
  running: (tk) => ACTIVE_STATES.has(tk.state) || tk.state === 'queued',
  waiting: (tk) => tk.state === 'awaiting_approval',
  done: (tk) => FINISHED_STATES.has(tk.state),
  scheduled: (tk) => tk.state === 'scheduled',
  // Done but stopped by an external cause (« BLOQUÉ : … », SPEC-EQUIPE §3).
  blocked: (tk) => tk.state === 'done' && !!tk.blocked,
};

const GROUP = { running: 0, executing: 0, awaiting_approval: 1, queued: 2, scheduled: 3 };

function ts(v) {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : Date.parse(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Sorts tasks for display: running first, then awaiting approval (oldest first), queued, scheduled
 * (soonest first), then finished tasks (most recent first). Returns a new array.
 */
export function sortTasks(list) {
  return [...list].sort((a, b) => {
    const ga = GROUP[a.state] ?? 4;
    const gb = GROUP[b.state] ?? 4;
    if (ga !== gb) return ga - gb;
    switch (ga) {
      case 0: return ts(b.startedAt) - ts(a.startedAt);
      case 1: return ts(a.waitingSince ?? a.updatedAt) - ts(b.waitingSince ?? b.updatedAt);
      case 2: return ts(a.createdAt) - ts(b.createdAt);
      case 3: return ts(a.dueAt) - ts(b.dueAt);
      default: return ts(b.finishedAt ?? b.updatedAt) - ts(a.finishedAt ?? a.updatedAt);
    }
  });
}

/**
 * Tasks visible in the panel for a scope. view 'building' ignores floorId.
 * @param {Iterable<object>} tasks
 * @param {{floorId?: string|null, deptId?: string|null, filter?: string, view?: string}} scope
 */
export function filterTasks(tasks, { floorId = null, deptId = null, filter = 'all', view = 'floor' } = {}) {
  const pred = TASK_FILTERS[filter] || TASK_FILTERS.all;
  const out = [];
  for (const tk of tasks) {
    if (view !== 'building' && floorId && tk.floorId !== floorId) continue;
    if (deptId && tk.deptId !== deptId) continue;
    if (!pred(tk)) continue;
    out.push(tk);
  }
  return out;
}

/** Counts per chip filter for a list of tasks. */
export function countByFilter(list) {
  const counts = { all: 0, running: 0, waiting: 0, done: 0, scheduled: 0, blocked: 0 };
  for (const tk of list) {
    counts.all += 1;
    for (const f of ['running', 'waiting', 'done', 'scheduled', 'blocked']) if (TASK_FILTERS[f](tk)) counts[f] += 1;
  }
  return counts;
}

function isSameDay(ms, now) {
  const a = new Date(ms);
  const b = new Date(now);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Local fallback for GET /api/stats: per department {doing, waiting, next, doneToday, failedToday}
 * and per floor {doing, waiting, doneToday}, from the task summaries known to the client.
 */
export function computeStats(tasks, now = Date.now()) {
  const departments = {};
  const floors = {};
  for (const tk of tasks) {
    const d = (departments[tk.deptId] ||= { doing: 0, waiting: 0, next: 0, doneToday: 0, failedToday: 0 });
    const f = (floors[tk.floorId] ||= { doing: 0, waiting: 0, doneToday: 0 });
    const finished = ts(tk.finishedAt ?? tk.updatedAt);
    const today = finished > 0 && isSameDay(finished, now);
    if (ACTIVE_STATES.has(tk.state)) {
      d.doing += 1;
      f.doing += 1;
    } else if (tk.state === 'awaiting_approval') {
      d.waiting += 1;
      f.waiting += 1;
    } else if (UPCOMING_STATES.has(tk.state)) {
      d.next += 1;
    } else if (tk.state === 'done' && today) {
      d.doneToday += 1;
      f.doneToday += 1;
    } else if (tk.state === 'failed' && today && !tk.ignored && !tk.superseded) {
      // A failure ignored by a person or resolved by a later run of its routine is no failure.
      d.failedToday += 1;
    }
  }
  return { departments, floors, source: 'local' };
}

/**
 * Applies a task transition to the top-bar counts ({awaitingApproval, running, queued}).
 * prev/next are task summaries or null (created / removed). Never goes below zero.
 */
export function applyCountsDelta(counts, prev, next) {
  const out = { awaitingApproval: 0, running: 0, queued: 0, ...counts };
  const bucket = (tk) => {
    if (!tk) return null;
    if (tk.state === 'awaiting_approval') return 'awaitingApproval';
    if (ACTIVE_STATES.has(tk.state)) return 'running';
    if (tk.state === 'queued') return 'queued';
    return null;
  };
  const a = bucket(prev);
  const b = bucket(next);
  if (a === b) return out;
  if (a) out[a] = Math.max(0, out[a] - 1);
  if (b) out[b] += 1;
  return out;
}

/** Default expected run duration when nothing is known (SPEC §12). */
export const DEFAULT_EXPECTED_MS = 90000;

/**
 * Live progress (0–100) of a task for display. For running tasks, extrapolates the server's
 * `progress` (computed as elapsed / expected) from the time the summary was received, capped at 95
 * and never below the server value. Other states: scheduled/queued 0, awaiting/done 100,
 * failed/cancelled/rejected keep the last value.
 * @param {object} task summary (startedAt, progress, state, _at = client receipt time)
 * @param {number} [now] client clock (ms)
 * @param {number} [offset] server clock − client clock (ms)
 */
export function liveProgress(task, now = Date.now(), offset = 0) {
  const p = Math.max(0, Math.min(100, Number(task?.progress) || 0));
  switch (task?.state) {
    case 'scheduled': case 'queued': return 0;
    case 'awaiting_approval': case 'done': return 100;
    case 'running': case 'executing': break;
    default: return p;
  }
  const started = ts(task.startedAt);
  if (!started) return Math.min(95, p);
  const serverNow = now + offset;
  const received = (task._at ?? now) + offset;
  const elapsedAtReceipt = received - started;
  let expected = p >= 1 && elapsedAtReceipt > 0 ? elapsedAtReceipt / (p / 100) : DEFAULT_EXPECTED_MS;
  expected = Math.max(5000, Math.min(4 * 3600 * 1000, expected));
  const live = ((serverNow - started) / expected) * 100;
  return Math.max(Math.min(95, p), Math.min(95, live));
}

// ---------------------------------------------------------------------------------------------
// Access levels (client-side mirror of SPEC §8, used only to show or hide controls)

export const LEVELS = ['view', 'operate', 'manage', 'admin'];

/** Rank of a level (-1 for none). */
export function levelRank(level) {
  return LEVELS.indexOf(level);
}

/** True when `level` is at least `need`. */
export function canLevel(level, need) {
  return levelRank(level) >= 0 && levelRank(level) >= levelRank(need);
}

/**
 * Client-side permission check from the `level` fields of bootstrap floors/departments.
 * @param {object} state store state
 * @param {'view'|'operate'|'manage'|'admin'} need
 * @param {{floorId?: string, deptId?: string}} [scope]
 */
export function can(state, need, { floorId, deptId } = {}) {
  if (state.me?.role === 'admin') return true;
  if (need === 'admin') return false;
  const { departments = [], floors = [] } = state.office || {};
  if (deptId) {
    const d = departments.find((x) => x.id === deptId);
    return !!d && canLevel(d.level, need);
  }
  if (floorId) {
    const levels = departments.filter((x) => x.floorId === floorId).map((x) => x.level);
    const f = floors.find((x) => x.id === floorId);
    if (f?.level) levels.push(f.level);
    return levels.some((l) => canLevel(l, need));
  }
  return false;
}

// ---------------------------------------------------------------------------------------------
// Office helpers

/** Floors sorted by `order` then name. */
export function sortedFloors(floors) {
  return [...(floors || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.name).localeCompare(String(b.name), 'fr'));
}

/** Departments of a floor sorted by `order`. */
export function floorDepartments(departments, floorId) {
  return (departments || []).filter((d) => d.floorId === floorId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.name).localeCompare(String(b.name), 'fr'));
}

/**
 * Agents of a department: the lead first (the plan gives it the desk facing the team), then the
 * others in the order the admin chose (their order in office.agents, PUT
 * /api/departments/:id/agent-order), like the office plan (web/scene/layout.js organizeOffice).
 */
export function deptAgents(agents, deptId) {
  return (agents || []).filter((a) => a.deptId === deptId)
    .sort((a, b) => Number(!!b.lead) - Number(!!a.lead));
}

/**
 * Active (not paused) routines per department, for the department cards.
 * @param {object[]} list
 * @returns {Record<string, number>}
 */
export function activeRoutineCounts(list) {
  const out = {};
  for (const r of list || []) if (r && !r.paused && r.deptId) out[r.deptId] = (out[r.deptId] || 0) + 1;
  return out;
}

/**
 * Suggestions waiting for the administrator's decision (status `proposed`): the badge of the top-bar
 * SUGGESTIONS button and the hint of its user-menu entry.
 * @param {object[]} list
 * @returns {number}
 */
export function pendingSuggestionCount(list) {
  let n = 0;
  for (const s of list || []) if (s && s.status === 'proposed') n += 1;
  return n;
}

/**
 * True when "Je valide avant d'agir" must stay on for this person (SPEC §8): below the manage level
 * the server refuses "auto" for an agent that asks for validation. With no agent chosen (routing),
 * the agent is only known after routing, so only an all-autonomous department allows "auto".
 * @param {object} state app state (me, office)
 * @param {{deptId: string, agentId?: string}} target
 */
export function approveLocked(state, { deptId, agentId = '' } = {}) {
  if (!deptId || can(state, 'manage', { deptId })) return false;
  const agents = deptAgents(state.office?.agents, deptId);
  const chosen = agentId ? agents.find((a) => a.id === agentId) : null;
  if (chosen) return chosen.autonomy !== 'auto';
  return !(agents.length && agents.every((a) => a.autonomy === 'auto'));
}

// ---------------------------------------------------------------------------------------------
// Hash routes

/** Where a task drawer was opened from (?from=…), to go back there when it is closed. */
const TASK_FROM = { taches: 'kanban', activite: 'activity' };
const OVERLAY_ROUTES = new Set(['task', 'agent', 'calendar', 'kanban', 'automations', 'admin', 'atelier', 'approvals', 'studio', 'studioItem', 'suggestions', 'connectors']);

/**
 * Parses a location hash: #/etage/<n> · #/building · #/task/<id>[?from=taches|activite] · #/agent/<id>[/<tab>] ·
 * #/calendar · #/taches (the tasks board: {name: 'kanban'}) · #/automations[/<tab>] · #/admin[/<tab>] ·
 * #/atelier · #/approvals · #/setup[?token=…] ·
 * #/etage/<n>/studio[/<deptId>] (the floor's studios, or one service's) · #/studio/item/<id>[/arbre|/fiche] ·
 * #/suggestions[/<status filter>] (the panel checks the filter: an unknown one opens its default) ·
 * #/connectors[/<connectorId>] (the office's connectors; with an id, that library connector's window or row).
 * A task opened from the board carries `from: 'kanban'` (its drawer goes back to the board when closed),
 * one opened from Administration → Activité (?from=activite) `from: 'activity'` (back to that tab).
 * A floor is addressed by its number (the one the elevator shows, 1-based: floorHash); the old
 * #/floor/<id> links still parse ({name: 'floor', id}) and are redirected by the app.
 * An agent tab is overview · routines · tasks · chat · settings; the drawer checks it (resolveTab in
 * web/ui/agent/model.js: an unknown or forbidden one opens the overview).
 * @param {string} hash
 * @returns {{name: string, id?: string, number?: number, tab?: string, token?: string, overlay: boolean, key: string}}
 */
export function parseRoute(hash) {
  const raw = String(hash || '').replace(/^#\/?/, '');
  const [pathPart, queryPart = ''] = raw.split('?');
  const parts = pathPart.split('/').filter(Boolean).map((p) => {
    try { return decodeURIComponent(p); } catch { return p; }
  });
  const query = new URLSearchParams(queryPart);
  const [name = 'home', a, b, c, d] = parts;
  let route;
  switch (name) {
    case 'etage': {
      const n = /^\d{1,4}$/.test(a || '') ? Number(a) : 0;
      if (n >= 1 && b === 'studio') route = { name: 'studio', number: n, id: c || null };
      else route = n >= 1 ? { name: 'floor', number: n } : { name: 'home' };
      break;
    }
    case 'studio':
      // #/studio/item/<id>[/arbre|/fiche], and an element shown inside its ad's page: #/studio/item/<pub>/v/<element>.
      if (a === 'item' && b && c === 'v' && d) route = { name: 'studioItem', id: b, tab: 'v', sub: d };
      else route = a === 'item' && b ? { name: 'studioItem', id: b, tab: c === 'arbre' || c === 'fiche' ? c : null } : { name: 'home' };
      break;
    case 'floor': route = a ? { name, id: a } : { name: 'home' }; break;
    case 'building': route = { name }; break;
    case 'task': {
      const from = query.get('from');
      route = a ? { name, id: a, ...(from && Object.hasOwn(TASK_FROM, from) ? { from: TASK_FROM[from] } : {}) } : { name: 'home' };
      break;
    }
    case 'taches': route = { name: 'kanban' }; break;
    case 'agent': route = a ? { name, id: a, tab: b || null } : { name: 'home' }; break;
    case 'calendar': case 'atelier': case 'approvals': route = { name }; break;
    case 'automations': case 'admin': case 'suggestions': route = { name, tab: a || null }; break;
    case 'connectors': route = { name, id: a || null }; break;
    case 'setup': route = { name, token: query.get('token') || a || '' }; break;
    default: route = { name: 'home' };
  }
  route.overlay = OVERLAY_ROUTES.has(route.name);
  route.key = [route.name, route.id, route.number, route.tab, route.sub].filter(Boolean).join('/');
  return route;
}

/** Builds a hash for a route object ({name, id?, tab?, sub?}); a floor by number → #/etage/<n>; the board → #/taches. */
export function routeHash({ name, id, number, tab, sub } = {}) {
  if (name === 'floor' && Number.isInteger(number) && number >= 1) return `#/etage/${number}`;
  if (name === 'kanban') return '#/taches';
  if (name === 'studio' && Number.isInteger(number) && number >= 1) return `#/etage/${number}/studio${id ? `/${encodeURIComponent(id)}` : ''}`;
  if (name === 'studioItem' && id && tab === 'v' && sub) return `#/studio/item/${encodeURIComponent(id)}/v/${encodeURIComponent(sub)}`;
  if (name === 'studioItem' && id) return `#/studio/item/${encodeURIComponent(id)}${tab === 'arbre' || tab === 'fiche' ? `/${tab}` : ''}`;
  const seg = [name, id, tab].filter(Boolean).map((s) => encodeURIComponent(s));
  return seg.length ? `#/${seg.join('/')}` : '#/';
}

/**
 * The number a floor shows in the elevator and the building (1-based: its order + 1, else its
 * rank in sortedFloors), or null when the floor is unknown.
 * @param {object[]} floors
 * @param {string} floorId
 */
export function floorNumberOf(floors, floorId) {
  const list = sortedFloors(floors);
  const idx = list.findIndex((f) => f.id === floorId);
  return idx < 0 ? null : floorNumber(list[idx], idx);
}

/**
 * The link of a floor's studio (#/etage/<n>/studio) or of one service's (…/studio/<deptId>).
 * @param {object[]} floors
 * @param {string} floorId
 * @param {string|null} [deptId]
 */
export function studioHash(floors, floorId, deptId = null) {
  const n = floorNumberOf(floors, floorId);
  return n ? routeHash({ name: 'studio', number: n, id: deptId || null }) : '#/';
}

/** The floor showing number `n` (first one when two share it), or null. */
export function floorByNumber(floors, n) {
  const list = sortedFloors(floors);
  return list.find((f, idx) => floorNumber(f, idx) === n) || null;
}

/**
 * The link of a floor: #/etage/<its number>. An unknown floor keeps the old id form
 * (#/floor/<id>), which the app resolves once the office is loaded.
 * @param {object[]} floors
 * @param {string} floorId
 */
export function floorHash(floors, floorId) {
  const n = floorNumberOf(floors, floorId);
  return n ? `#/etage/${n}` : `#/floor/${encodeURIComponent(floorId)}`;
}

/**
 * The floor a floor route points to: by number (#/etage/2) or, for the old links, by id
 * (#/floor/ecom-us) — a digits-only old link that is no floor id is read as a number.
 * @param {object[]} floors
 * @param {{name: string, id?: string, number?: number}} route
 * @returns {object|null}
 */
export function floorOfRoute(floors, route) {
  if (!route || route.name !== 'floor') return null;
  if (route.number) return floorByNumber(floors, route.number);
  const byId = (floors || []).find((f) => f.id === route.id);
  if (byId) return byId;
  return /^\d{1,4}$/.test(String(route.id || '')) ? floorByNumber(floors, Number(route.id)) : null;
}
